'use client';

import { useMemo } from 'react';

import { useSubsidiaryFilterOptional } from '@/@subsidiary/context';
import type { SubsidiaryFilterContextValue, SubsidiaryOption } from '@/@subsidiary/types';

/**
 * Who the credit screens are for in this tab (docs/pdlc/credits-ui.md §7 "View-as").
 *
 * - `client`: the signed-in person's own org. Only the client hooks
 *   (`/credit-consumption/**`) may fetch.
 * - `view-as`: a staff member viewing as a client. Only the admin hooks
 *   (`/admin/orgs/{orgId}/credit-consumption/**`) may fetch, for `orgId`.
 * - `pending`: a View-as session is restoring, starting or ending, so nobody's
 *   credits may be fetched yet. Fetching the client hooks here would read the
 *   staff member's own org (B11).
 *
 * Every client hook is `enabled` only in `client` mode and every admin hook only
 * in `view-as` mode.
 */
export type CreditsViewerMode = 'client' | 'view-as' | 'pending';

export interface CreditsViewer {
    mode: CreditsViewerMode;
    /** The client's Studio org id under View-as; null otherwise. */
    orgId: string | null;
    /**
     * Under View-as, the name of the org whose credits are shown (`orgId`), when
     * known. A subsidiary picked within the hierarchy is named, not the entry org.
     */
    orgName: string | null;
    /**
     * Whether teammates' names may be shown. Under View-as staff see the client
     * admin's view (D13), which names teammates. For a client the server scopes
     * what it returns (D6, `resolveCreditViewer`), so the screen shows what it gets:
     * while the API's `credits-member-scoping` flag is off it sends `canSeeNames`
     * true for everyone, and the screen is today's.
     */
    canSeeNames: boolean;
}

type ViewerFilter = Pick<
    SubsidiaryFilterContextValue,
    'isInternal' | 'masqueradeOrgId' | 'selectedOrgIds' | 'viewAsStatus' | 'viewAsOrgName'
> & {
    hierarchyOptions?: NamedOrg[];
    options?: NamedOrg[];
};

type NamedOrg = Pick<SubsidiaryOption, 'orgId' | 'orgName'>;

export const CLIENT_VIEWER: CreditsViewer = {
    mode: 'client',
    orgId: null,
    orgName: null,
    canSeeNames: true,
};

const PENDING_VIEWER: CreditsViewer = {
    mode: 'pending',
    orgId: null,
    orgName: null,
    canSeeNames: false,
};

/** Pure: the viewer for a subsidiary-filter state (null outside the provider). */
export function creditsViewerOf(filter: ViewerFilter | null | undefined): CreditsViewer {
    if (!filter) return CLIENT_VIEWER;
    const { viewAsStatus } = filter;
    if (viewAsStatus === 'restoring' || viewAsStatus === 'starting' || viewAsStatus === 'ending') {
        return PENDING_VIEWER;
    }
    if (!filter.isInternal || filter.masqueradeOrgId == null) return CLIENT_VIEWER;

    // Same target rule as useAccountViewAsTarget: one selected org within the
    // masqueraded hierarchy narrows it, otherwise the masqueraded org itself.
    const selected = filter.selectedOrgIds;
    const target =
        selected?.length === 1 && selected[0] != null ? selected[0] : filter.masqueradeOrgId;
    return {
        mode: 'view-as',
        orgId: String(target),
        orgName: targetOrgName(filter, target),
        canSeeNames: true,
    };
}

/**
 * The shown org's name: from the hierarchy or option lists (as
 * useAccountViewAsTarget reads it), else the session's name, which is the
 * entry org's and so only right when the entry org is the one shown.
 */
function targetOrgName(filter: ViewerFilter, target: number): string | null {
    const listed =
        filter.hierarchyOptions?.find((o) => o.orgId === target)?.orgName ??
        filter.options?.find((o) => o.orgId === target)?.orgName ??
        null;
    if (listed) return listed;
    return target === filter.masqueradeOrgId ? (filter.viewAsOrgName ?? null) : null;
}

export function useCreditsViewer(): CreditsViewer {
    const filter = useSubsidiaryFilterOptional();
    const hasFilter = filter != null;
    const isInternal = filter?.isInternal === true;
    const masqueradeOrgId = filter?.masqueradeOrgId ?? null;
    const selectedSingle =
        filter?.selectedOrgIds?.length === 1 ? (filter.selectedOrgIds[0] ?? null) : null;
    const viewAsStatus = filter?.viewAsStatus ?? 'idle';
    const viewAsOrgName = filter?.viewAsOrgName ?? null;
    const shownOrgId = selectedSingle ?? masqueradeOrgId;
    const listedName =
        shownOrgId == null
            ? null
            : (filter?.hierarchyOptions?.find((o) => o.orgId === shownOrgId)?.orgName ??
              filter?.options?.find((o) => o.orgId === shownOrgId)?.orgName ??
              null);

    // Primitive deps keep one reference while the answer is unchanged.
    return useMemo(
        () =>
            creditsViewerOf(
                hasFilter
                    ? {
                          isInternal,
                          masqueradeOrgId,
                          selectedOrgIds: selectedSingle != null ? [selectedSingle] : [],
                          viewAsStatus,
                          viewAsOrgName,
                          options:
                              shownOrgId != null && listedName
                                  ? [{ orgId: shownOrgId, orgName: listedName }]
                                  : [],
                      }
                    : null,
            ),
        [
            hasFilter,
            isInternal,
            masqueradeOrgId,
            selectedSingle,
            viewAsStatus,
            viewAsOrgName,
            shownOrgId,
            listedName,
        ],
    );
}
